import { Fragment } from 'react';
import { useGo } from '../../app/navigation';
import { useSubPath } from '../../app/useSubPath';
import { useSettings } from '../../core/store';
import { Btn } from '../../ui/Button';
import { Icon } from '../../ui/icons';
import { modStyle } from '../../ui/style';
import { ExerciseRunner } from '../exercise/ExerciseRunner';
import { LessonPage } from '../lesson/LessonPage';
import { GroupTitle, LessonList, LevelGrid, ModulePage, type TabDef } from '../module/ModulePage';
import { EX_GROUPS, LEVELS, levelConfig, type VokalLevel } from './levels';
import { RangeTest } from './RangeTest';
import { VoicePicker } from './VoicePicker';
import { range, rangeText } from './voice';
import { Warmup } from './Warmup';

type Tab = 'dersler' | 'antrenman' | 'rutin';
const TABS: TabDef<Tab>[] = [
  { id: 'dersler', label: 'Dersler', icon: 'book' },
  { id: 'antrenman', label: 'Antrenman', icon: 'target' },
  { id: 'rutin', label: 'Isınma', icon: 'voice' },
];

function LevelRoute({ level }: { level: VokalLevel }) {
  const go = useGo();
  useSettings();
  // breath levels measure time, not pitch: they need no range
  if (!range() && level.group.id !== 'nefes')
    return (
      <div className="ex-page">
        <button type="button" className="link-back" onClick={() => go('vokal.antrenman')}>
          <Icon name="left" />
          <span>Vokal &amp; Şan</span>
        </button>
        <div className="ex-start-card" style={modStyle('vokal')}>
          <div className="eyebrow">
            {level.group.title} · {level.title}
          </div>
          <h1 className="ex-start-title">Önce ses aralığın</h1>
          <VoicePicker />
        </div>
      </div>
    );
  return <ExerciseRunner config={() => levelConfig(level)} />;
}

export function VokalSection() {
  const go = useGo();
  useSettings();
  const [sub, a, b] = useSubPath();
  if (sub === 'ders') return <LessonPage module="vokal" id={a ?? ''} />;
  if (sub === 'egz') {
    const level = LEVELS.find((l) => l.group.id === a && String(l.n) === b);
    if (level) return <LevelRoute level={level} />;
  }
  if (sub === 'aralik-testi') return <RangeTest />;
  if (sub === 'isinma') return <Warmup />;
  const tab: Tab = sub === 'antrenman' || sub === 'rutin' ? sub : 'dersler';
  return (
    <ModulePage id="vokal" levels={LEVELS} tabs={TABS} active={tab}>
      {tab === 'dersler' ? (
        <>
          <aside className="tip t-warn" style={{ marginBottom: '16px' }}>
            <Icon name="alert" />
            <div>
              <strong className="tip-title">Sesini koru</strong>
              <p>
                Ağrı, yanma ya da zorlanma hissedersen dur. İki-üç haftadan uzun süren ses kısıklığında bir KBB uzmanına
                görün.
              </p>
            </div>
          </aside>
          <GroupTitle title="Önce öğren" sub="Nefesten vibratoya, sağlıklı ve doğru söylemenin temelleri." />
          <LessonList module="vokal" />
        </>
      ) : tab === 'antrenman' ? (
        <>
          <div className="panel" style={{ marginBottom: '8px' }}>
            <div className="mb-row">
              <strong>Ses aralığın: </strong>
              <span>{rangeText()}</span>
              <span className="mb-sp" />
              <Btn kind={range() ? 'ghost' : 'primary'} onClick={() => go('vokal.aralik-testi')}>
                {range() ? 'Değiştir' : 'Belirle'}
              </Btn>
            </div>
          </div>
          {EX_GROUPS.map((g) => (
            <Fragment key={g.id}>
              <GroupTitle title={g.title} sub={g.sub} />
              <LevelGrid items={g.levels} />
            </Fragment>
          ))}
        </>
      ) : (
        <>
          <GroupTitle
            title="Isınma ve test"
            sub="Her çalışmadan önce ısın; ses aralığını bir kez test et, egzersizler ona göre ayarlansın."
          />
          <div className="tool-grid">
            <button type="button" className="tool-big" onClick={() => go('vokal.isinma')}>
              <span className="tb-ic">
                <Icon name="lungs" />
              </span>
              <h3>Günlük ısınma</h3>
              <p>Nefes, dudak titreşimi, gam, arpej, siren ve soğuma: altı adımlık rutin.</p>
            </button>
            <button type="button" className="tool-big" onClick={() => go('vokal.aralik-testi')}>
              <span className="tb-ic">
                <Icon name="voice" />
              </span>
              <h3>Ses aralığı testi</h3>
              <p>En kalın ve en ince rahat notanı bul, ses tipini öğren. Şu an: {rangeText()}.</p>
            </button>
            <button type="button" className="tool-big" onClick={() => go('araclar.analiz')}>
              <span className="tb-ic">
                <Icon name="wave" />
              </span>
              <h3>Canlı ses analizi</h3>
              <p>Söylediğin notayı, sapmasını ve vibratonu canlı izle.</p>
            </button>
          </div>
        </>
      )}
    </ModulePage>
  );
}
